import { LockOutlined, LogoutOutlined, TranslationOutlined } from '@ant-design/icons';
import { PageContainer, ProLayout } from '@ant-design/pro-components';
import { useLocalStorageState } from 'ahooks';
import { Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation } from 'react-router-dom';
import store from 'store2';

/**
 * Contexts
 */
import { useConfigStore } from '@/store/config';
import { useLayoutStore } from '@/store/layout';

/**
 * Types
 */
import type { UserDto } from '@/dtos/user.dto';
import type { PageContainerProps, ProLayoutProps } from '@ant-design/pro-components';
import type { DropdownProps } from 'antd';

/**
 * Constants
 */
import routes from '@/router/routes';

const Layout: React.FC = () => {
    /**
     * Hooks
     */
    const location = useLocation();

    const { t } = useTranslation();

    /**
     * States
     */
    const { setLocale } = useConfigStore();

    const { layout, pageContainer } = useLayoutStore();

    const [user] = useLocalStorageState<UserDto>('user');

    /**
     * ChildrenProps
     */
    const dropdownProps: DropdownProps = {
        menu: {
            items: [
                {
                    children: [
                        {
                            icon: <span>🇨🇳</span>,
                            key: 'zh',
                            label: '简体中文',
                        },
                        {
                            icon: <span>🇺🇸</span>,
                            key: 'en',
                            label: 'English',
                        },
                    ],
                    icon: <TranslationOutlined />,
                    key: 'changeLanguage',
                    label: t('changeLanguage'),
                    onClick: ({ key }) => setLocale(key),
                },
                {
                    type: 'divider',
                },
                {
                    icon: <LockOutlined />,
                    key: 'changePassword',
                    label: t('changePassword'),
                },
                {
                    icon: <LogoutOutlined />,
                    key: 'logout',
                    label: t('logout'),
                    onClick: () => {
                        store.remove('accessToken');
                        store.remove('permissions');
                        store.remove('roles');
                        store.remove('user');

                        window.location.reload();
                    },
                },
            ],
        },
        overlayStyle: {
            minWidth: 160,
        },
        placement: 'bottomRight',
    };

    const layoutProps: ProLayoutProps = {
        avatarProps: {
            children: user?.nickname?.charAt(0).toUpperCase(),
            size: 'small',
            src: user?.avatar ? `${import.meta.env.VITE_CDN}/${user.avatar}` : undefined,
            title: user?.nickname,
            render: (_, dom) => <Dropdown {...dropdownProps}>{dom}</Dropdown>,
        },
        collapsed: false,
        collapsedButtonRender: false,
        disableMobile: true,
        layout: 'mix',
        location,
        logo: '/logo.svg',
        route: { routes },
        splitMenus: true,
        title: 'Dashboard',
        menuFooterRender: () => `v${__VERSION__}`,
        menuItemRender: (item, dom) => <Link to={item.path as string}>{dom}</Link>,
        ...layout,
    };

    const pageContainerProps: PageContainerProps = {
        childrenContentStyle: {
            paddingBlockEnd: 24,
            paddingInline: 24,
        },
        header: {
            style: {
                paddingInline: 24,
            },
        },
        ...pageContainer,
    };

    return (
        <ProLayout {...layoutProps}>
            <PageContainer {...pageContainerProps}>
                <Outlet />
            </PageContainer>
        </ProLayout>
    );
};

export default Layout;
